import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { usePending } from "../hooks/usePending";
import { useToday } from "../hooks/useToday";
import { useNav } from "../hooks/useNav";
import { AREAS, areaBadge, locate } from "./navModel";
import Glyph from "./navGlyphs";

/*
 * THE SIDEBAR — Today plus four areas, per Gabriel's mockup (Sidebar.dc.html).
 *
 * ONE AREA OPEN AT A TIME, and the one holding the current page opens on its
 * own when the page changes. Clicking an area's row only opens or closes it; it
 * never navigates, so looking inside Meetings from the board costs nothing.
 *
 * A CLOSED AREA SUMS ITS CHILDREN'S BADGES, and an open one moves each badge
 * down to its child. That is how Approvals keeps its number visible without a
 * top-level item of its own: collapsed, Sales shows it.
 *
 * Today's badge is still its own count — everything due across the system — not
 * the sum of the areas. The two differ on purpose: Today counts what is due
 * now, and the area badges count queues.
 */

function Badge({ n }) {
  return n > 0 ? <span className="crm-side__badge crm-num">{n}</span> : null;
}

export default function Sidebar() {
  const { count: pending } = usePending();
  const { total: today } = useToday();
  const { followUps, hint } = useNav();
  const { pathname, search } = useLocation();
  const here = locate(pathname, search, hint);
  const badges = { pending, followups: followUps };

  // The open area follows the page; a click can then open or close any area
  // until the page changes again.
  const [open, setOpen] = useState(here.area);
  useEffect(() => {
    setOpen(AREAS.some((a) => a.id === here.area) ? here.area : null);
  }, [here.area, pathname]);

  return (
    <aside className="crm-sidebar">
      <div className="crm-side__logo">
        <span className="crm-side__mark">
          <img src="/favicon.svg" alt="Remotion" />
        </span>
        <span className="crm-side__product">CRM</span>
      </div>

      <nav className="crm-side__nav" aria-label="Main">
        <Link to="/today" className={`crm-side__top${here.area === "today" ? " is-active" : ""}`}>
          <Glyph name="sun" />
          <span className="crm-side__label">Today</span>
          <Badge n={today} />
        </Link>

        <div className="crm-side__gap" />

        {AREAS.map((area) => {
          const isOpen = open === area.id;
          const holds = here.area === area.id;
          return (
            <div key={area.id} className="crm-side__area">
              <button
                type="button"
                className={`crm-side__head${holds ? " holds" : ""}`}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : area.id)}
              >
                <Glyph name={area.glyph} />
                <span className="crm-side__label">{area.label}</span>
                {!isOpen && <Badge n={areaBadge(area, badges)} />}
                <Glyph name={isOpen ? "chevronDown" : "chevronRight"} size={16} className="crm-side__chev" />
              </button>
              {isOpen && (
                <div className="crm-side__items">
                  {area.items.map((item) =>
                    item.soon ? (
                      <span key={item.key} className="crm-side__item is-soon" aria-disabled="true" title="Not built yet">
                        <span className="crm-side__label">{item.label}</span>
                        <span className="crm-side__soon">Soon</span>
                      </span>
                    ) : (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={`crm-side__item${holds && here.item === item.key ? " is-active" : ""}`}
                    >
                      <span className="crm-side__label">{item.label}</span>
                      {item.badge && <Badge n={badges[item.badge]} />}
                    </Link>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="crm-side__foot">
        <Link to="/settings" className={`crm-side__me${here.area === "settings" ? " is-active" : ""}`} title="Settings">
          <span className="crm-side__avatar">GE</span>
          <span className="crm-side__who">
            <b>Gabriel</b>
            <span>Founder</span>
          </span>
          <Glyph name="settings" size={16} className="crm-side__cog" />
        </Link>
      </div>
    </aside>
  );
}
